import React from 'react';
import Box from '@mui/material/Box';
import StepShell, { PrimaryBtn, SelectionChip } from './StepShell';
import { QuantityBreakdown, QuestionLogicalId, QuizAnswer } from './types';
import { TagCategory } from '../../services/panacea/types';
import { Theme, useMediaQuery, useTheme } from '@mui/material';
import UserDetailsForm from './UserDetailsForm';
import { useRouter } from 'next/router';
import useNonNullContext from '../../hooks/useNonNullContext';
import ConfigContext from '../../context/config';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import UserContext from '../../context/user';
import { getProspectFormSchema } from '../../utils/prospect-form-schema';
import { IngredientToAvoid } from '../../ts/@types/generated/graphql/catalog-service/graphql';
import _ from 'lodash';
import {
  HIDDEN_LINKED_INGREDIENT_IDS,
  LINKED_INGREDIENT_IDS,
} from './constants';
import useAnalytics from '../../analytics/useAnalytics';

interface StepReviewProps {
  setAnswers: React.Dispatch<
    React.SetStateAction<Record<string, Partial<QuizAnswer>>>
  >;
  answers: Record<string, Partial<QuizAnswer>>;
  onNext: (zipCode: string, name?: string, email?: string) => void;
  onBack?: (() => void) | null;
  stepIdx: number;
  total: number;
  jumpTo: (idOrIdx: QuestionLogicalId | number) => void;
  questionLogicalIds: QuestionLogicalId[];
  isSubmitting: boolean;
  tags: TagCategory['tags'];
  ingredientsToAvoid: IngredientToAvoid[];
}

export default function Review({
  answers,
  setAnswers,
  onNext,
  onBack,
  stepIdx,
  total,
  jumpTo,
  questionLogicalIds,
  isSubmitting,
  tags,
  ingredientsToAvoid,
}: StepReviewProps) {
  const { user } = useNonNullContext(UserContext);
  const router = useRouter();
  const tenantConfig = useNonNullContext(ConfigContext);
  // Get query param for menu entry version override (takes precedence over tenant config)
  // This determines which fields will be shown in the user details form component.
  const menuEntryOverride = router.query.menuEntry as
    'ZIP' | 'EMAIL_ZIP' | 'FULL' | undefined;
  const formVariant =
    menuEntryOverride || tenantConfig.menuPage?.menuEntryModalConfig?.version;
  const schema = getProspectFormSchema(formVariant);
  const { control, handleSubmit } = useForm<{
    zipCode: string;
    email?: string;
    name?: string;
  }>({
    mode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: {
      zipCode: '',
      email: user?.email ?? '',
      name: '',
    },
  });

  const theme = useTheme();
  const isSmallScreen = useMediaQuery<Theme>((t) => t.breakpoints.down('sm'));

  const quantitySummary = () => {
    if (answers['QUANTITY_BY_MEAL_TYPE']) {
      const quantityBreakdown = answers['QUANTITY_BY_MEAL_TYPE']
        ?.mealQuantityByMealType as QuantityBreakdown;
      const parts: string[] = [];
      if (quantityBreakdown.breakfast)
        parts.push(
          `${quantityBreakdown.breakfast} ${quantityBreakdown.breakfast === 1 ? 'breakfast' : 'breakfasts'}`,
        );
      if (quantityBreakdown.lunch)
        parts.push(
          `${quantityBreakdown.lunch} ${quantityBreakdown.lunch === 1 ? 'lunch' : 'lunches'}`,
        );
      if (quantityBreakdown.dinner)
        parts.push(
          `${quantityBreakdown.dinner} ${quantityBreakdown.dinner === 1 ? 'dinner' : 'dinners'}`,
        );
      return parts.join(', ') || '—';
    }

    if (answers['QUANTITY']) {
      return `${answers['QUANTITY'].mealQuantity ?? 0} meals`;
    }

    return '—';
  };

  const removeIngredientToAvoid = (
    answerLogicalId: QuestionLogicalId,
    ingredientId: number,
  ) => {
    setAnswers((prevAnswers) => {
      const updatedAnswers = { ...prevAnswers };
      const answer = updatedAnswers[answerLogicalId];
      const ingredientsToAvoidIds = answer?.ingredientToAvoidIds || [];
      const linkedIds = LINKED_INGREDIENT_IDS[ingredientId] ?? [];
      const allIds = [ingredientId, ...linkedIds];

      if (answer && ingredientsToAvoid) {
        answer.ingredientToAvoidIds = ingredientsToAvoidIds.filter(
          (id) => !allIds.includes(id),
        );
      }
      return updatedAnswers;
    });
  };

  const { track } = useAnalytics();

  const submit = () => {
    track('Personalized Plan Completed');
    handleSubmit(({ zipCode, name, email }) => {
      onNext(zipCode, name, email);
    })();
  };

  return (
    <StepShell
      stepIdx={stepIdx}
      total={total}
      mobile={isSmallScreen}
      onBack={onBack}
      footer={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <UserDetailsForm control={control} formVariant={formVariant} />
          <PrimaryBtn onClick={submit} disabled={isSubmitting}>
            See my plan
          </PrimaryBtn>
        </Box>
      }
    >
      <Box sx={{ pt: isSmallScreen ? '4px' : '8px', pb: '16px' }}>
        <Box
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: isSmallScreen ? 30 : 40,
            lineHeight: 1.08,
            m: 0,
            mb: '6px',
          }}
        >
          Your plan
        </Box>
        <Box
          component="p"
          sx={{
            fontSize: isSmallScreen ? 15 : 17,
            m: 0,
            mb: isSmallScreen ? '20px' : '26px',
          }}
        >
          Tap any answer to change it.
        </Box>

        {/* Quantity hero */}
        {(questionLogicalIds.includes('QUANTITY') ||
          questionLogicalIds.includes('QUANTITY_BY_MEAL_TYPE')) && (
          <Box
            component="button"
            onClick={() => jumpTo('QUANTITY')}
            sx={{
              width: '100%',
              textAlign: 'left',
              border: 0,
              borderRadius: '16px',
              p: '20px 22px',
              fontFamily: 'inherit',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '14px',
              mb: '18px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}
              >
                Your plan
              </Box>
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? 26 : 32,
                  lineHeight: 1.1,
                  letterSpacing: '-0.015em',
                }}
              >
                {quantitySummary()}
              </Box>
            </Box>

            <Box
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: theme.palette.primary.contrastText,
                background: theme.palette.primary.main,
                px: '12px',
                py: '6px',
                borderRadius: 999,
              }}
              onClick={() => {
                if (questionLogicalIds.includes('QUANTITY_BY_MEAL_TYPE')) {
                  jumpTo('QUANTITY_BY_MEAL_TYPE');
                  return;
                }

                jumpTo('QUANTITY');
              }}
            >
              Edit
            </Box>
          </Box>
        )}

        {questionLogicalIds.includes('EATING_STYLE') && (
          <ReviewSection
            title="Eating style"
            onEdit={() => jumpTo('EATING_STYLE')}
          >
            {!answers['EATING_STYLE']?.tagIds?.length ? (
              <Box
                component="span"
                sx={{ color: theme.palette.text.secondary, fontSize: 14 }}
              >
                No preference.
              </Box>
            ) : (
              <SelectionChip
                label={
                  tags.find(
                    (t) => t.id === answers['EATING_STYLE']?.tagIds?.[0],
                  )?.displayName || ''
                }
                selected
                onClick={() => jumpTo('EATING_STYLE')}
              />
            )}
          </ReviewSection>
        )}

        {questionLogicalIds.includes('PRIMARY_PROTEIN') && (
          <ReviewSection
            title="Primary protein"
            onEdit={() => jumpTo('PRIMARY_PROTEIN')}
          >
            {!answers['PRIMARY_PROTEIN']?.tagIds?.length ? (
              <Box
                component="span"
                sx={{ color: theme.palette.text.secondary, fontSize: 14 }}
              >
                No preference.
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {answers['PRIMARY_PROTEIN']?.tagIds?.map((tagId) => (
                  <SelectionChip
                    key={tagId}
                    label={tags.find((t) => t.id === tagId)?.displayName || ''}
                    selected
                    onClick={() => jumpTo('PRIMARY_PROTEIN')}
                  />
                ))}
              </Box>
            )}
          </ReviewSection>
        )}

        {questionLogicalIds.includes('INGREDIENTS_TO_AVOID') && (
          <ReviewSection
            title="Anything to avoid"
            onEdit={() => jumpTo('INGREDIENTS_TO_AVOID')}
          >
            {answers['INGREDIENTS_TO_AVOID']?.ingredientToAvoidIds?.filter(
              (id) => !HIDDEN_LINKED_INGREDIENT_IDS.includes(id),
            ).length === 0 ? (
              <Box
                component="span"
                sx={{ color: theme.palette.text.secondary, fontSize: 14 }}
              >
                Nothing avoided.
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {answers['INGREDIENTS_TO_AVOID']?.ingredientToAvoidIds
                  ?.filter((id) => !HIDDEN_LINKED_INGREDIENT_IDS.includes(id))
                  .map((ingredientId) => {
                    const ingredient = ingredientsToAvoid.find(
                      (ingredient) =>
                        ingredient.legacyAllergenItemId === ingredientId,
                    );
                    if (!ingredient) {
                      return;
                    }

                    return (
                      <RemovableChip
                        key={ingredientId}
                        label={_.capitalize(ingredient.displayName) || ''}
                        onRemove={() =>
                          removeIngredientToAvoid(
                            'INGREDIENTS_TO_AVOID',
                            ingredientId,
                          )
                        }
                      />
                    );
                  })}
              </Box>
            )}
          </ReviewSection>
        )}

        {questionLogicalIds.includes('COMMON_ALLERGENS') && (
          <ReviewSection
            title="Anything to avoid"
            onEdit={() => jumpTo('COMMON_ALLERGENS')}
          >
            {answers['COMMON_ALLERGENS']?.ingredientToAvoidIds?.filter(
              (id) => !HIDDEN_LINKED_INGREDIENT_IDS.includes(id),
            ).length === 0 ? (
              <Box
                component="span"
                sx={{ color: theme.palette.text.secondary, fontSize: 14 }}
              >
                Nothing avoided.
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {answers['COMMON_ALLERGENS']?.ingredientToAvoidIds
                  ?.filter((id) => !HIDDEN_LINKED_INGREDIENT_IDS.includes(id))
                  .map((ingredientId) => {
                    const ingredient = ingredientsToAvoid.find(
                      (ingredient) =>
                        ingredient.legacyAllergenItemId === ingredientId,
                    );
                    if (!ingredient) {
                      return;
                    }

                    return (
                      <RemovableChip
                        key={ingredientId}
                        label={_.capitalize(ingredient.displayName) || ''}
                        onRemove={() =>
                          removeIngredientToAvoid(
                            'COMMON_ALLERGENS',
                            ingredientId,
                          )
                        }
                      />
                    );
                  })}
              </Box>
            )}
          </ReviewSection>
        )}
      </Box>
    </StepShell>
  );
}

function ReviewSection({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ mb: '18px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: '10px',
        }}
      >
        <Box
          component="span"
          sx={{
            fontWeight: 600,
            fontSize: 17,
            letterSpacing: '-0.005em',
          }}
        >
          {title}
        </Box>
        <Box
          component="button"
          onClick={onEdit}
          sx={{
            fontSize: 13,
            fontWeight: 600,
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            p: 0,
          }}
        >
          Edit
        </Box>
      </Box>
      <Box>{children}</Box>
    </Box>
  );
}

function RemovableChip({
  label,
  fromTemplate,
  onRemove,
}: {
  label: string;
  fromTemplate?: string | null;
  onRemove: () => void;
}) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        p: '6px 6px 6px 12px',
        borderRadius: 999,
        background: '#FFFFFF',
        border: '1.5px solid #E5E5EE',
        fontSize: 13,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
      >
        <span>{label}</span>
        {fromTemplate && (
          <Box
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.04em',
              px: '6px',
              py: '2px',
              borderRadius: 999,
              background: 'rgba(4,28,44,0.06)',
            }}
          >
            from {fromTemplate}
          </Box>
        )}
      </Box>
      <Box
        component="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        sx={{
          width: 20,
          height: 20,
          borderRadius: 999,
          border: 0,
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 700,
          fontFamily: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ×
      </Box>
    </Box>
  );
}
